import { useEffect, useState } from "react";
import api from "@/lib/api";
import { StaffHeader } from "@/components/StaffHeader";
import { Button } from "@/components/ui/button";
import { Printer, HeartPulse, FileDown } from "lucide-react";
import { downloadElementPdf } from "@/lib/pdf";

const optLabel = (o) => (typeof o === "string" ? o : o.label);

function Choices({ q }) {
  const opts = q.options || [];
  const max = q.type === "multi_choice" ? (q.max_choices || 2) : null;
  return (
    <div className="mt-1.5">
      <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
        {opts.map((o, i) => (
          <div key={i} className="flex items-center gap-2 text-[13px] text-slate-800">
            <span className="inline-block h-3.5 w-3.5 border border-slate-500 rounded-[3px] shrink-0" /> {optLabel(o)}
          </div>
        ))}
      </div>
      {max && <p className="text-[11px] text-slate-500 mt-1">{max} choix maximum.</p>}
    </div>
  );
}

function Question({ n, q, note }) {
  return (
    <div className="mb-3 break-inside-avoid">
      <p className="text-sm font-semibold text-slate-900">{n}. {q.texte}{note && <span className="font-normal italic text-slate-500"> — {note}</span>}</p>
      {Array.isArray(q.options) && q.options.length ? <Choices q={q} /> : (
        <div className="mt-1 border-b border-dotted border-slate-400 h-6" />
      )}
    </div>
  );
}

export default function FicheBilanImprimable() {
  const [quest, setQuest] = useState(null);
  const [error, setError] = useState(false);
  const [pdfing, setPdfing] = useState(false);

  useEffect(() => {
    api.get("/prevention/questionnaire").then((r) => setQuest(r.data)).catch(() => setError(true));
  }, []);

  const savePdf = async () => {
    setPdfing(true);
    try { await downloadElementPdf(document.getElementById("fiche-sheet"), "fiche-bilan-prevention.pdf"); }
    finally { setPdfing(false); }
  };

  return (
    <div className="min-h-screen bg-muted/20">
      <div className="print:hidden"><StaffHeader /></div>

      <div className="mx-auto max-w-3xl px-4 py-6">
        <div className="flex items-center justify-between mb-5 print:hidden">
          <div>
            <h1 className="font-heading text-2xl font-bold">Fiche bilan de prévention (à imprimer)</h1>
            <p className="text-sm text-muted-foreground">Imprimez cette fiche vierge et remettez-la au patient au comptoir. Aucune donnée n'est enregistrée sur le site.</p>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            <Button data-testid="fiche-pdf" onClick={savePdf} disabled={pdfing || !quest} variant="outline" className="rounded-xl gap-2 h-11"><FileDown className="h-4 w-4" /> {pdfing ? "Génération…" : "Enregistrer en PDF"}</Button>
            <Button data-testid="fiche-print" onClick={() => window.print()} className="rounded-xl gap-2 h-11"><Printer className="h-4 w-4" /> Imprimer</Button>
          </div>
        </div>

        {error && <p className="text-sm text-destructive">Impossible de charger le questionnaire.</p>}
        {!quest && !error && <p className="text-sm text-muted-foreground">Chargement…</p>}

        {quest && (
          <div id="fiche-sheet" className="bg-white text-slate-900 rounded-xl border shadow-sm p-8 print:shadow-none print:border-0 print:rounded-none print:p-0" data-testid="fiche-sheet">
            <div className="flex items-center gap-2 mb-1">
              <div className="h-9 w-9 rounded-xl bg-slate-900 grid place-items-center text-white"><HeartPulse className="h-5 w-5" /></div>
              <div className="font-heading font-bold leading-tight text-lg">Bilan de prévention — Pharmacie des Écoles</div>
            </div>
            <p className="text-[12px] text-slate-500 mb-4">Fiche remplie au comptoir avec le pharmacien. Document confidentiel — ne pas laisser sans surveillance.</p>

            <div className="grid grid-cols-3 gap-4 mb-5">
              <div><p className="text-[11px] uppercase tracking-wide text-slate-500">Nom</p><div className="border-b border-slate-400 h-6" /></div>
              <div><p className="text-[11px] uppercase tracking-wide text-slate-500">Prénom</p><div className="border-b border-slate-400 h-6" /></div>
              <div><p className="text-[11px] uppercase tracking-wide text-slate-500">Date de naissance</p><div className="border-b border-slate-400 h-6" /></div>
            </div>

            <h2 className="font-heading font-bold text-slate-900 border-b border-slate-300 pb-1 mb-3">Questionnaire de prévention</h2>
            {(quest.principal || []).map((q, i) => <Question key={q.id} n={i + 1} q={q} />)}

            {(quest.complementary || []).length > 0 && (
              <>
                <h2 className="font-heading font-bold text-slate-900 border-b border-slate-300 pb-1 mb-3 mt-5">Questions complémentaires (si concerné)</h2>
                {(quest.complementary || []).map((q, i) => (
                  <Question key={q.id} n={(quest.principal || []).length + i + 1} q={q} note="selon la situation du patient" />
                ))}
              </>
            )}

            <div className="mt-6 pt-3 border-t border-slate-300 grid grid-cols-2 gap-6">
              <div><p className="text-[11px] uppercase tracking-wide text-slate-500 mb-6">Priorités retenues avec le patient</p><div className="border-b border-slate-400" /></div>
              <div><p className="text-[11px] uppercase tracking-wide text-slate-500 mb-6">Date &amp; signature du pharmacien</p><div className="border-b border-slate-400" /></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
